import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";
import React, { useEffect, useMemo, useState } from "react";
import {
    ActionIcon,
    Badge,
    Button,
    Divider,
    Group,
    Loader,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    ThemeIcon,
    Title,
} from "@mantine/core";
import {
    IconBuildingStore,
    IconCheck,
    IconEdit,
    IconFileInvoice,
    IconPlus,
    IconTrash,
    IconX,
} from "@tabler/icons-react";

const API_URL = process.env.REACT_APP_API_URL ?? "";

type Event = {
    eventID: number;
    eventName: string;
};

type EventBar = {
    eventBarId: number;
    eventId: number;
    name: string;
    isActive: boolean;
};

type Invoice = {
    invoiceId: number;
    eventId: number;
    name: string;
    isActive: boolean;
};

type DeleteTarget =
    | {
        type: "bar";
        id: number;
        name: string;
    }
    | {
        type: "invoice";
        id: number;
        name: string;
    };

export default function EventBars() {
    const [events, setEvents] = useState<Event[]>([]);
    const [eventId, setEventId] = useState<number | null>(null);

    const [bars, setBars] = useState<EventBar[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    // =========================
    // BAR MODAL
    // =========================

    const [barModalOpen, setBarModalOpen] = useState(false);
    const [editingBar, setEditingBar] = useState<EventBar | null>(null);

    const [barForm, setBarForm] = useState({
        name: "",
        isActive: true,
    });

    // =========================
    // INVOICE MODAL
    // =========================

    const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
    const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);

    const [invoiceForm, setInvoiceForm] = useState({
        name: "",
        isActive: true,
    });

    // =========================
    // DELETE MODAL
    // =========================

    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

    // =========================
    // SELECTED EVENT
    // =========================

    const selectedEvent = useMemo(
        () => events.find((event) => event.eventID === eventId),
        [events, eventId]
    );

    // =========================
    // LOAD EVENTS
    // =========================

    const loadEvents = async () => {
        try {
            const res = await fetch(`${API_URL}/api/events`);

            if (!res.ok) {
                throw new Error(
                    `Could not load events: ${res.status} ${res.statusText}`
                );
            }

            const data = await res.json();

            setEvents(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setEvents([]);
        }
    };

    // =========================
    // LOAD BARS
    // =========================

    const loadBars = async (id: number) => {
        try {
            const res = await fetch(`${API_URL}/api/EventBars/event/${id}`);

            if (!res.ok) {
                throw new Error(
                    `Could not load bars: ${res.status} ${res.statusText}`
                );
            }

            const data = await res.json();

            setBars(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setBars([]);
        }
    };

    // =========================
    // LOAD INVOICES
    // =========================

    const loadInvoices = async (id: number) => {
        try {
            const res = await fetch(`${API_URL}/api/invoices/event/${id}`);

            if (!res.ok) {
                throw new Error(
                    `Could not load invoices: ${res.status} ${res.statusText}`
                );
            }

            const data = await res.json();

            setInvoices(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error(error);
            setInvoices([]);
        }
    };

    // =========================
    // LOAD EVENT DATA
    // =========================

    const loadEventData = async (id: number) => {
        setLoading(true);

        try {
            await Promise.all([loadBars(id), loadInvoices(id)]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEvents();
    }, []);

    useEffect(() => {
        if (eventId !== null) {
            loadEventData(eventId);
        } else {
            setBars([]);
            setInvoices([]);
        }

        closeBarModal();
        closeInvoiceModal();
    }, [eventId]);

    // =========================
    // BAR MODAL
    // =========================

    const openCreateBar = () => {
        setEditingBar(null);

        setBarForm({
            name: "",
            isActive: true,
        });

        setBarModalOpen(true);
    };

    const openEditBar = (bar: EventBar) => {
        setEditingBar(bar);

        setBarForm({
            name: bar.name,
            isActive: bar.isActive,
        });

        setBarModalOpen(true);
    };

    const closeBarModal = () => {
        setBarModalOpen(false);
        setEditingBar(null);

        setBarForm({
            name: "",
            isActive: true,
        });
    };

    // =========================
    // SAVE BAR
    // =========================

    const saveBar = async () => {
        if (!eventId || !barForm.name.trim()) {
            return;
        }

        setSaving(true);

        try {
            if (editingBar) {
                const updatedBar: EventBar = {
                    ...editingBar,
                    name: barForm.name.trim(),
                    isActive: barForm.isActive,
                };

                const res = await fetch(
                    `${API_URL}/api/EventBars/${editingBar.eventBarId}`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(updatedBar),
                    }
                );

                if (!res.ok) {
                    throw new Error("Could not update bar.");
                }
            } else {
                const res = await fetch(`${API_URL}/api/EventBars`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        eventId,
                        name: barForm.name.trim(),
                        isActive: barForm.isActive,
                    }),
                });

                if (!res.ok) {
                    throw new Error("Could not create bar.");
                }
            }

            closeBarModal();
            await loadBars(eventId);
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    // =========================
    // INVOICE MODAL
    // =========================

    const openCreateInvoice = () => {
        setEditingInvoice(null);

        setInvoiceForm({
            name: "",
            isActive: true,
        });

        setInvoiceModalOpen(true);
    };

    const openEditInvoice = (invoice: Invoice) => {
        setEditingInvoice(invoice);

        setInvoiceForm({
            name: invoice.name,
            isActive: invoice.isActive,
        });

        setInvoiceModalOpen(true);
    };

    const closeInvoiceModal = () => {
        setInvoiceModalOpen(false);
        setEditingInvoice(null);

        setInvoiceForm({
            name: "",
            isActive: true,
        });
    };

    // =========================
    // SAVE INVOICE
    // =========================

    const saveInvoice = async () => {
        if (!eventId || !invoiceForm.name.trim()) {
            return;
        }

        setSaving(true);

        try {
            if (editingInvoice) {
                const updatedInvoice: Invoice = {
                    ...editingInvoice,
                    name: invoiceForm.name.trim(),
                    isActive: invoiceForm.isActive,
                };

                const res = await fetch(
                    `${API_URL}/api/invoices/${editingInvoice.invoiceId}`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(updatedInvoice),
                    }
                );

                if (!res.ok) {
                    throw new Error("Could not update invoice.");
                }
            } else {
                const res = await fetch(`${API_URL}/api/invoices`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        eventId,
                        name: invoiceForm.name.trim(),
                        isActive: invoiceForm.isActive,
                    }),
                });

                if (!res.ok) {
                    throw new Error("Could not create invoice.");
                }
            }

            closeInvoiceModal();
            await loadInvoices(eventId);
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    // =========================
    // DELETE
    // =========================

    const requestDeleteBar = (bar: EventBar) => {
        setDeleteTarget({
            type: "bar",
            id: bar.eventBarId,
            name: bar.name,
        });
    };

    const requestDeleteInvoice = (invoice: Invoice) => {
        setDeleteTarget({
            type: "invoice",
            id: invoice.invoiceId,
            name: invoice.name,
        });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) {
            return;
        }

        setDeleting(true);

        try {
            const endpoint =
                deleteTarget.type === "bar"
                    ? `${API_URL}/api/EventBars/${deleteTarget.id}`
                    : `${API_URL}/api/invoices/${deleteTarget.id}`;

            const res = await fetch(endpoint, {
                method: "DELETE",
            });

            if (!res.ok) {
                throw new Error(
                    `Could not delete ${deleteTarget.type === "bar" ? "bar" : "invoice"
                    }.`
                );
            }

            if (deleteTarget.type === "bar") {
                setBars((prev) =>
                    prev.filter((bar) => bar.eventBarId !== deleteTarget.id)
                );
            } else {
                setInvoices((prev) =>
                    prev.filter(
                        (invoice) => invoice.invoiceId !== deleteTarget.id
                    )
                );
            }

            setDeleteTarget(null);
        } catch (error) {
            console.error(error);
        } finally {
            setDeleting(false);
        }
    };

    // =========================
    // STATUS CONTROL
    // =========================

    const toggleBarStatus = async (bar: EventBar) => {
        const updatedBar = {
            ...bar,
            isActive: !bar.isActive,
        };

        try {
            const res = await fetch(
                `${API_URL}/api/EventBars/${bar.eventBarId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatedBar),
                }
            );

            if (!res.ok) {
                throw new Error("Could not update bar.");
            }

            setBars((prev) =>
                prev.map((item) =>
                    item.eventBarId === bar.eventBarId ? updatedBar : item
                )
            );
        } catch (error) {
            console.error(error);
        }
    };

    const toggleInvoiceStatus = async (invoice: Invoice) => {
        const updatedInvoice = {
            ...invoice,
            isActive: !invoice.isActive,
        };

        try {
            const res = await fetch(
                `${API_URL}/api/invoices/${invoice.invoiceId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(updatedInvoice),
                }
            );

            if (!res.ok) {
                throw new Error("Could not update invoice.");
            }

            setInvoices((prev) =>
                prev.map((item) =>
                    item.invoiceId === invoice.invoiceId
                        ? updatedInvoice
                        : item
                )
            );
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div>
            <HeroSectionV2
                titleEn="Manage Bars and Invoices"
                titleDa="Administrer Bar og Faktura"
                descriptionEn="Create and manage bars and invoice types for your events"
                descriptionDa="Opret og administrer barer og fakturatyper til events"
                imageSrc={Billede1}
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    { label: "Admin", to: "/admin" },
                    { label: "Drinks" },
                ]}
            />

            <div className="content">
                {/* ================= EVENT SELECTOR ================= */}

                <Paper
                    withBorder
                    radius="lg"
                    p="xl"
                    mb="xl"
                    shadow="xs"
                >
                    <Group justify="space-between" align="flex-start" mb="lg">
                        <div>
                            <Title order={3}>Bars & Invoices</Title>

                            <Text c="dimmed" size="sm" mt={4}>
                                Select an event to configure bars and invoice
                                types.
                            </Text>
                        </div>

                        {selectedEvent && (
                            <Badge
                                size="lg"
                                variant="light"
                                color="blue"
                            >
                                {selectedEvent.eventName}
                            </Badge>
                        )}
                    </Group>

                    <Select
                        label="Event"
                        placeholder="Select event..."
                        value={eventId?.toString() ?? null}
                        onChange={(value) =>
                            setEventId(value ? Number(value) : null)
                        }
                        data={events.map((event) => ({
                            value: event.eventID.toString(),
                            label: event.eventName,
                        }))}
                        searchable
                        clearable
                        size="md"
                    />
                </Paper>

                {/* ================= NO EVENT ================= */}

                {!eventId && (
                    <Paper
                        withBorder
                        radius="lg"
                        p={50}
                        style={{
                            textAlign: "center",
                        }}
                    >
                        <ThemeIcon
                            color="red"
                            size={55}
                            radius="xl"
                            variant="light"
                            mx="auto"
                            mb="md"
                        >
                            <IconBuildingStore size={28} />
                        </ThemeIcon>

                        <Title order={4}>Select an event</Title>

                        <Text c="dimmed" mt={5}>
                            Choose an event above to manage bars and invoices.
                        </Text>
                    </Paper>
                )}

                {/* ================= LOADING ================= */}

                {eventId && loading && (
                    <Paper
                        withBorder
                        radius="lg"
                        p={50}
                        style={{
                            textAlign: "center",
                        }}
                    >
                        <Loader />

                        <Text c="dimmed" mt="md">
                            Loading event configuration...
                        </Text>
                    </Paper>
                )}

                {/* ================= CONTENT ================= */}

                {eventId && !loading && (
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
                        {/* ================= BARS ================= */}

                        <Paper
                            withBorder
                            radius="lg"
                            shadow="xs"
                            style={{
                                overflow: "hidden",
                            }}
                        >
                            <Group
                                justify="space-between"
                                p="lg"
                                align="center"
                            >
                                <Group gap="sm">
                                    <ThemeIcon
                                        color ="red"
                                        variant="light"
                                        size="lg"
                                        radius="md"
                                    >
                                        <IconBuildingStore size={20} />
                                    </ThemeIcon>

                                    <div>
                                        <Group gap="xs">
                                            <Title order={4}>Bars</Title>

                                            <Badge
                                                variant="light"
                                                color="gray"
                                                size="sm"
                                            >
                                                {bars.length}
                                            </Badge>
                                        </Group>

                                        <Text c="dimmed" size="xs">
                                            Sales locations for this event
                                        </Text>
                                    </div>
                                </Group>

                                <Button
                                    color="red"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={openCreateBar}
                                >
                                    Add bar
                                </Button>
                            </Group>

                            <Divider />

                            {bars.length === 0 ? (
                                <Stack
                                    align="center"
                                    justify="center"
                                    p={50}
                                    gap="xs"
                                >
                                    <ThemeIcon
                                        variant="light"
                                        color="gray"
                                        size={45}
                                        radius="xl"
                                    >
                                        <IconBuildingStore size={22} />
                                    </ThemeIcon>

                                    <Text fw={600}>No bars yet</Text>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                        ta="center"
                                    >
                                        Create the first bar for this event.
                                    </Text>

                                    <Button
                                        color="red"
                                        variant="light"
                                        mt="sm"
                                        leftSection={<IconPlus size={16} />}
                                        onClick={openCreateBar}
                                    >
                                        Add bar
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack gap={0}>
                                    {bars.map((bar, index) => (
                                        <React.Fragment key={bar.eventBarId}>
                                            <Group
                                                justify="space-between"
                                                p="md"
                                                wrap="nowrap"
                                            >
                                                <div>
                                                    <Text fw={600}>
                                                        {bar.name}
                                                    </Text>

                                                    <Badge
                                                        mt={5}
                                                        size="sm"
                                                        variant="light"
                                                        color={
                                                            bar.isActive
                                                                ? "green"
                                                                : "gray"
                                                        }
                                                        style={{
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            toggleBarStatus(bar)
                                                        }
                                                    >
                                                        {bar.isActive
                                                            ? "Active"
                                                            : "Inactive"}
                                                    </Badge>
                                                </div>

                                                <Group gap={6} wrap="nowrap">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        size="lg"
                                                        aria-label="Edit bar"
                                                        onClick={() =>
                                                            openEditBar(bar)
                                                        }
                                                    >
                                                        <IconEdit size={18} />
                                                    </ActionIcon>

                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        size="lg"
                                                        aria-label="Delete bar"
                                                        onClick={() =>
                                                            requestDeleteBar(bar)
                                                        }
                                                    >
                                                        <IconTrash size={18} />
                                                    </ActionIcon>
                                                </Group>
                                            </Group>

                                            {index < bars.length - 1 && (
                                                <Divider />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Stack>
                            )}
                        </Paper>

                        {/* ================= INVOICES ================= */}

                        <Paper
                            withBorder
                            radius="lg"
                            shadow="xs"
                            style={{
                                overflow: "hidden",
                            }}
                        >
                            <Group
                                justify="space-between"
                                p="lg"
                                align="center"
                            >
                                <Group gap="sm">
                                    <ThemeIcon
                                        variant="light"
                                        size="lg"
                                        radius="md"
                                    >
                                        <IconFileInvoice size={20} />
                                    </ThemeIcon>

                                    <div>
                                        <Group gap="xs">
                                            <Title order={4}>
                                                Invoices
                                            </Title>

                                            <Badge
                                                variant="light"
                                                color="gray"
                                                size="sm"
                                            >
                                                {invoices.length}
                                            </Badge>
                                        </Group>

                                        <Text c="dimmed" size="xs">
                                            Invoice types for this event
                                        </Text>
                                    </div>
                                </Group>

                                <Button
                                    color="red"
                                    leftSection={<IconPlus size={16} />}
                                    onClick={openCreateInvoice}
                                >
                                    Add invoice
                                </Button>
                            </Group>

                            <Divider />

                            {invoices.length === 0 ? (
                                <Stack
                                    align="center"
                                    justify="center"
                                    p={50}
                                    gap="xs"
                                >
                                    <ThemeIcon
                                        variant="light"
                                        color="gray"
                                        size={45}
                                        radius="xl"
                                    >
                                        <IconFileInvoice size={22} />
                                    </ThemeIcon>

                                    <Text fw={600}>No invoices yet</Text>

                                    <Text
                                        size="sm"
                                        c="dimmed"
                                        ta="center"
                                    >
                                        Create the first invoice type for this
                                        event.
                                    </Text>

                                    <Button
                                        color="red"
                                        variant="light"
                                        mt="sm"
                                        leftSection={<IconPlus size={16} />}
                                        onClick={openCreateInvoice}
                                    >
                                        Add invoice
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack gap={0}>
                                    {invoices.map((invoice, index) => (
                                        <React.Fragment
                                            key={invoice.invoiceId}
                                        >
                                            <Group
                                                justify="space-between"
                                                p="md"
                                                wrap="nowrap"
                                            >
                                                <div>
                                                    <Text fw={600}>
                                                        {invoice.name}
                                                    </Text>

                                                    <Badge
                                                        mt={5}
                                                        size="sm"
                                                        variant="light"
                                                        color={
                                                            invoice.isActive
                                                                ? "green"
                                                                : "gray"
                                                        }
                                                        style={{
                                                            cursor: "pointer",
                                                        }}
                                                        onClick={() =>
                                                            toggleInvoiceStatus(
                                                                invoice
                                                            )
                                                        }
                                                    >
                                                        {invoice.isActive
                                                            ? "Active"
                                                            : "Inactive"}
                                                    </Badge>
                                                </div>

                                                <Group gap={6} wrap="nowrap">
                                                    <ActionIcon
                                                        variant="subtle"
                                                        size="lg"
                                                        aria-label="Edit invoice"
                                                        onClick={() =>
                                                            openEditInvoice(
                                                                invoice
                                                            )
                                                        }
                                                    >
                                                        <IconEdit size={18} />
                                                    </ActionIcon>

                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="red"
                                                        size="lg"
                                                        aria-label="Delete invoice"
                                                        onClick={() =>
                                                            requestDeleteInvoice(
                                                                invoice
                                                            )
                                                        }
                                                    >
                                                        <IconTrash size={18} />
                                                    </ActionIcon>
                                                </Group>
                                            </Group>

                                            {index <
                                                invoices.length - 1 && (
                                                    <Divider />
                                                )}
                                        </React.Fragment>
                                    ))}
                                </Stack>
                            )}
                        </Paper>
                    </SimpleGrid>
                )}
            </div>

            {/* =====================================================
                BAR MODAL
            ===================================================== */}

            <Modal
                opened={barModalOpen}
                onClose={closeBarModal}
                title={
                    <Group gap="sm">
                        <ThemeIcon variant="light">
                            <IconBuildingStore size={18} />
                        </ThemeIcon>

                        <Text fw={700}>
                            {editingBar ? "Edit bar" : "Create bar"}
                        </Text>
                    </Group>
                }
                centered
                radius="lg"
            >
                <Stack>
                    <TextInput
                        label="Bar name"
                        placeholder="e.g. Main Bar"
                        value={barForm.name}
                        onChange={(event) =>
                            setBarForm((prev) => ({
                                ...prev,
                                name: event.currentTarget.value,
                            }))
                        }
                        autoFocus
                        required
                    />

                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                    >
                        <Group justify="space-between">
                            <div>
                                <Text fw={600} size="sm">
                                    Status
                                </Text>

                                <Text c="dimmed" size="xs">
                                    Is this bar available for use?
                                </Text>
                            </div>

                            <Button
                                variant="light"
                                color={
                                    barForm.isActive ? "green" : "gray"
                                }
                                leftSection={
                                    barForm.isActive ? (
                                        <IconCheck size={15} />
                                    ) : (
                                        <IconX size={15} />
                                    )
                                }
                                onClick={() =>
                                    setBarForm((prev) => ({
                                        ...prev,
                                        isActive: !prev.isActive,
                                    }))
                                }
                            >
                                {barForm.isActive
                                    ? "Active"
                                    : "Inactive"}
                            </Button>
                        </Group>
                    </Paper>

                    <Divider />

                    <Group justify="flex-end">
                        <Button
                            color="red"
                            variant="default"
                            onClick={closeBarModal}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="red"
                            loading={saving}
                            disabled={!barForm.name.trim()}
                            onClick={saveBar}
                        >
                            {editingBar ? "Save changes" : "Create bar"}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* =====================================================
                INVOICE MODAL
            ===================================================== */}

            <Modal
                opened={invoiceModalOpen}
                onClose={closeInvoiceModal}
                title={
                    <Group gap="sm">
                        <ThemeIcon variant="light">
                            <IconFileInvoice size={18} />
                        </ThemeIcon>

                        <Text fw={700}>
                            {editingInvoice
                                ? "Edit invoice"
                                : "Create invoice"}
                        </Text>
                    </Group>
                }
                centered
                radius="lg"
            >
                <Stack>
                    <TextInput
                        label="Invoice name"
                        placeholder="e.g. VIP Invoice"
                        value={invoiceForm.name}
                        onChange={(event) =>
                            setInvoiceForm((prev) => ({
                                ...prev,
                                name: event.currentTarget.value,
                            }))
                        }
                        autoFocus
                        required
                    />

                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                    >
                        <Group justify="space-between">
                            <div>
                                <Text fw={600} size="sm">
                                    Status
                                </Text>

                                <Text c="dimmed" size="xs">
                                    Is this invoice type available for use?
                                </Text>
                            </div>

                            <Button
                                variant="light"
                                color={
                                    invoiceForm.isActive
                                        ? "green"
                                        : "gray"
                                }
                                leftSection={
                                    invoiceForm.isActive ? (
                                        <IconCheck size={15} />
                                    ) : (
                                        <IconX size={15} />
                                    )
                                }
                                onClick={() =>
                                    setInvoiceForm((prev) => ({
                                        ...prev,
                                        isActive: !prev.isActive,
                                    }))
                                }
                            >
                                {invoiceForm.isActive
                                    ? "Active"
                                    : "Inactive"}
                            </Button>
                        </Group>
                    </Paper>

                    <Divider />

                    <Group justify="flex-end">
                        <Button
                            color="red"
                            variant="default"
                            onClick={closeInvoiceModal}
                        >
                            Cancel
                        </Button>

                        <Button
                        color="red"
                            loading={saving}
                            disabled={!invoiceForm.name.trim()}
                            onClick={saveInvoice}
                        >
                            {editingInvoice
                                ? "Save changes"
                                : "Create invoice"}
                        </Button>
                    </Group>
                </Stack>
            </Modal>

            {/* =====================================================
                DELETE CONFIRMATION
            ===================================================== */}

            <Modal
                opened={deleteTarget !== null}
                onClose={() => {
                    if (!deleting) {
                        setDeleteTarget(null);
                    }
                }}
                centered
                radius="lg"
                title={
                    <Group gap="sm">
                        <ThemeIcon
                            color="red"
                            variant="light"
                            radius="xl"
                        >
                            <IconTrash size={18} />
                        </ThemeIcon>

                        <Text fw={700}>
                            Delete{" "}
                            {deleteTarget?.type === "bar"
                                ? "bar"
                                : "invoice"}
                            ?
                        </Text>
                    </Group>
                }
            >
                <Stack>
                    <Text>
                        Are you sure you want to delete{" "}
                        <Text span fw={700}>
                            "{deleteTarget?.name}"
                        </Text>
                        ?
                    </Text>

                    <Paper
                        p="md"
                        radius="md"
                        style={{
                            background: "var(--mantine-color-red-light)",
                        }}
                    >
                        <Text
                            size="sm"
                            c="red"
                            fw={600}
                        >
                            This action cannot be undone.
                        </Text>
                    </Paper>

                    <Group justify="flex-end" mt="sm">
                        <Button
                            color="red"
                            variant="default"
                            disabled={deleting}
                            onClick={() => setDeleteTarget(null)}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="red"
                            loading={deleting}
                            leftSection={<IconTrash size={16} />}
                            onClick={confirmDelete}
                        >
                            Delete{" "}
                            {deleteTarget?.type === "bar"
                                ? "bar"
                                : "invoice"}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
}